import { setLocalStorage } from "@app/core/localStorage/localStorage";
import { clearToken, setToken } from "./token-store";
import { revokeAndLogout } from "./logout";

/**
 * IIDM-3067 T3 — the staff app's OAuth authorization-code flow with PKCE (RFC 7636, S256) and a
 * `state` nonce, against the IIDM-3063 PUBLIC SSO client.
 *
 * Before: the authorize redirect carried no `state` and no `code_challenge`, and the code exchange
 * posted the OAuth client secret — a value compiled into the public JS bundle, so anyone holding the bundle
 * could act as this client. Now:
 *
 *   - authorize: `client_id, redirect_uri, response_type=code, state, code_challenge,
 *     code_challenge_method=S256` (URLSearchParams — properly encoded);
 *   - callback: `state` must equal the nonce this tab stored, or NO token request is made (login
 *     CSRF / code injection);
 *   - token POST: `grant_type=authorization_code, client_id, redirect_uri, code, code_verifier` —
 *     no secret.
 *
 * The SSO issues NO refresh token to a public client (IIDM-3063 T5; a refresh attempt is a 400
 * `invalid_grant`). When the access token expires the app re-runs this authorize redirect, which the
 * live SSO session answers without a prompt — see `reauthorize()`.
 *
 * `state` is the nonce ONLY. The deep link the user was heading for is still kept the way it always
 * was — `localStorage.payload.smart_link`, consumed by permission-api-component.tsx (plan).
 */

export const STATE_KEY = "oauth_state";
export const VERIFIER_KEY = "pkce_verifier";
/** Set when the app auto-retried authorize after a failed callback; stops a retry loop. */
export const RETRY_KEY = "oauth_retry";
/** Epoch ms of the last 401-driven re-authorize; a second 401 soon after logs out instead. */
export const REAUTH_AT_KEY = "oauth_reauth_at";
export const REAUTH_LOOP_WINDOW_MS = 60 * 1000;

/**
 * @param {Uint8Array} bytes raw bytes
 * @return {string} base64url without padding (RFC 7636 §3 / appendix A)
 */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * @param {number} byteLength number of random bytes
 * @return {string} base64url of that many CSPRNG bytes
 */
export function randomUrlSafe(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/**
 * @param {string} verifier the PKCE code verifier
 * @return {Promise<string>} base64url(SHA-256(ascii(verifier))) — the S256 code challenge
 */
export async function s256Challenge(verifier: string): Promise<string> {
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

function sessionGet(key: string): string {
  try {
    return window.sessionStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
}

function sessionSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // storage blocked: the callback will fail the state check, which is the safe outcome.
  }
}

function sessionRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
}

/**
 * The state + verifier pair for this tab's pending authorize. An unconsumed pair is REUSED rather
 * than regenerated, so two authorize starts in one tab (a double mount, a 401 racing the login
 * component) cannot overwrite the pair the in-flight redirect will come back with.
 *
 * @return {{state: string, verifier: string}} the pending pair
 */
export function getOrCreatePendingPair(): { state: string; verifier: string } {
  let state = sessionGet(STATE_KEY);
  let verifier = sessionGet(VERIFIER_KEY);
  if (!state || !verifier) {
    state = randomUrlSafe(32);
    verifier = randomUrlSafe(32); // 43 chars, inside RFC 7636's 43..128
    sessionSet(STATE_KEY, state);
    sessionSet(VERIFIER_KEY, verifier);
  }
  return { state, verifier };
}

/**
 * @param {string} state the nonce
 * @param {string} challenge the S256 challenge
 * @return {string} the full SSO authorize URL
 */
export function buildAuthorizeUrl(state: string, challenge: string): string {
  const query = new URLSearchParams({
    client_id: `${process.env.REACT_APP_CLIENT_ID}`,
    redirect_uri: `${process.env.REACT_APP_REDIRECT_URI}`,
    response_type: "code",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  return `${process.env.REACT_APP_AUTHORIZATION_URL}?${query.toString()}`;
}

/**
 * Sends the browser to the SSO authorize endpoint. Keeps the existing deep-link mechanism: when the
 * user is not at "/", `localStorage.payload` gets the same shape it always had.
 *
 * @param {string} returnTo the path to land on after login
 * @return {Promise<void>} resolves once the navigation was requested
 */
export async function startAuthorize(returnTo: string = window.location.pathname): Promise<void> {
  const { state, verifier } = getOrCreatePendingPair();
  const challenge = await s256Challenge(verifier);
  if (returnTo && returnTo !== "/") {
    setLocalStorage(
      "payload",
      JSON.stringify({
        client_id: `${process.env.REACT_APP_CLIENT_ID}`,
        redirect_uri: `${process.env.REACT_APP_REDIRECT_URI}`,
        response_type: "code",
        smart_link: returnTo,
      })
    );
  }
  window.location.href = buildAuthorizeUrl(state, challenge);
}

/**
 * Validates an SSO callback. The stored pair is consumed (removed) whatever the outcome, so a
 * code/state can never be replayed into this tab.
 *
 * @param {string} search `window.location.search`
 * @return {{code: string, verifier: string} | null} null when there is no code or `state` does not
 *   match the nonce this tab stored
 */
export function consumeCallback(search: string): { code: string; verifier: string } | null {
  const params = new URLSearchParams(search);
  const code = params.get("code") || "";
  const returnedState = params.get("state") || "";
  const expectedState = sessionGet(STATE_KEY);
  const verifier = sessionGet(VERIFIER_KEY);
  sessionRemove(STATE_KEY);
  sessionRemove(VERIFIER_KEY);
  if (!code || !returnedState || !expectedState || !verifier || returnedState !== expectedState) {
    return null;
  }
  return { code, verifier };
}

/**
 * Exchanges the code for an access token at the SSO (`${REACT_APP_SERVER_URL}/token`). No secret.
 * Only `access_token` is kept; a public client gets no refresh token, and none would be stored.
 *
 * @param {string} code the authorization code
 * @param {string} verifier the PKCE verifier paired with the challenge sent at authorize
 * @return {Promise<boolean>} true when a token was stored
 */
export async function exchangeCode(code: string, verifier: string): Promise<boolean> {
  try {
    const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/token`, {
      method: "POST",
      body: JSON.stringify({
        grant_type: "authorization_code",
        client_id: `${process.env.REACT_APP_CLIENT_ID}`,
        redirect_uri: `${process.env.REACT_APP_REDIRECT_URI}`,
        code,
        code_verifier: verifier,
      }),
      headers: { "content-type": "application/json" },
    });
    const json = await res.json();
    if (json && typeof json.access_token === "string" && json.access_token) {
      setToken(json.access_token);
      sessionRemove(RETRY_KEY);
      return true;
    }
  } catch (err) {
    console.error("IIDM-3067: SSO code exchange failed", err);
  }
  clearToken();
  return false;
}

/**
 * After a failed callback (bad state, failed exchange): retry the authorize ONCE automatically —
 * the SSO session usually answers it silently — and report failure the second time so a broken
 * environment cannot loop the browser.
 *
 * @return {boolean} true when a retry was started, false when the caller should show an error
 */
export function retryAuthorizeOnce(): boolean {
  if (sessionGet(RETRY_KEY)) {
    sessionRemove(RETRY_KEY);
    return false;
  }
  sessionSet(RETRY_KEY, "1");
  startAuthorize("/");
  return true;
}

let reauthInFlight = false;

/**
 * The 401 path (axios interceptor). There is no refresh token, so an expired or revoked access
 * token is replaced by re-running the PKCE authorize redirect — silent while the SSO session lives,
 * the login page otherwise. The user's current page is kept as the deep link.
 *
 * Loop guard: a second 401 within REAUTH_LOOP_WINDOW_MS of a re-authorize means a FRESH token is
 * being refused too (e.g. the account lost access) — re-authorizing again would loop forever, so
 * that case logs out (revoke + clear + SSO logout) instead.
 */
export function reauthorize(): void {
  // The flag is never cleared: both branches end in a navigation that unloads the page, and every
  // other request that 401s in the meantime must not start a second run (or trip the loop guard).
  if (reauthInFlight) {
    return;
  }
  reauthInFlight = true;
  const last = Number(sessionGet(REAUTH_AT_KEY)) || 0;
  if (last && Date.now() - last < REAUTH_LOOP_WINDOW_MS) {
    sessionRemove(REAUTH_AT_KEY);
    revokeAndLogout();
    return;
  }
  sessionSet(REAUTH_AT_KEY, String(Date.now()));
  clearToken();
  startAuthorize(window.location.pathname);
}

/** Test seam: a real page never needs this, because the navigation unloads the module. */
export function resetReauthStateForTests(): void {
  reauthInFlight = false;
}
